import { Link, Navigate, useLocation } from 'react-router-dom';
import { clinic } from '../data/site.js';

const legalPages = {
  'privacy-policy': {
    eyebrow: 'Privacy',
    title: 'Privacy Policy',
    intro:
      'This policy explains how My Dentist handles information shared through this website, appointment forms, phone calls, email and WhatsApp enquiries.',
    updated: 'Last updated: July 31, 2026',
    sections: [
      {
        heading: 'Information we collect',
        body: [
          'When you submit an appointment request, we may collect your name, phone number, email address, preferred date and time, selected treatment concern, notes you choose to share and basic technical details needed to operate the website.',
          'Please do not send sensitive medical reports through the form unless the clinic specifically asks for them.'
        ]
      },
      {
        heading: 'How we use it',
        body: [
          'We use submitted details to respond to enquiries, schedule appointments, avoid duplicate booking slots, send appointment updates and maintain clinic records.',
          'Your details are not sold or rented to third parties.'
        ]
      },
      {
        heading: 'Communication',
        body: [
          'The clinic may contact you by phone, email or WhatsApp about your appointment request. You can ask the clinic to stop non-essential follow-up messages.'
        ]
      },
      {
        heading: 'Data protection',
        body: [
          'Access to appointment records is limited to authorised clinic users. Website and admin access should be protected using strong passwords and hosting-level security controls.'
        ]
      }
    ]
  },
  'cookie-policy': {
    eyebrow: 'Cookies',
    title: 'Cookie Policy',
    intro:
      'This page explains how this website uses cookies and local browser storage to provide a stable visitor experience.',
    updated: 'Last updated: July 31, 2026',
    sections: [
      {
        heading: 'Essential cookies',
        body: [
          'Essential cookies and browser storage may be used for site security, form behaviour, admin sessions and remembering cookie consent choices.'
        ]
      },
      {
        heading: 'Analytics and marketing cookies',
        body: [
          'Analytics or advertising cookies should only be enabled after the clinic connects the relevant tools and consent settings. The current website consent banner is designed to support that setup.'
        ]
      },
      {
        heading: 'Managing cookies',
        body: [
          'You can clear or block cookies in your browser settings. Some website features, especially forms or admin login, may not work correctly if essential cookies are disabled.'
        ]
      }
    ]
  },
  accessibility: {
    eyebrow: 'Accessibility',
    title: 'Accessibility',
    intro:
      'My Dentist aims to keep this website clear, readable and usable for patients looking for dental care information in Varanasi.',
    updated: 'Last updated: July 31, 2026',
    sections: [
      {
        heading: 'Our approach',
        body: [
          'The website uses semantic page structure, labelled form fields, readable contrast, responsive layouts and keyboard-accessible links and buttons where possible.'
        ]
      },
      {
        heading: 'Known limitations',
        body: [
          'Some third-party embeds, such as map providers, may have accessibility limitations outside the clinic website code.'
        ]
      },
      {
        heading: 'Request help',
        body: [
          `If you have trouble using any page, contact the clinic at ${clinic.phoneDisplay} or ${clinic.emailDisplay}. The team can help with appointment booking or clinic information directly.`
        ]
      }
    ]
  }
};

export default function LegalPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, '');
  const page = legalPages[slug];

  if (!page) return <Navigate to="/" replace />;

  return (
    <main className="legal-page">
      <div className="container legal-page__inner">
        <Link className="legal-page__back" to="/">Back to home</Link>
        <p className="eyebrow">{page.eyebrow}</p>
        <h1 className="heading-display">{page.title}</h1>
        <p className="legal-page__intro">{page.intro}</p>
        <p className="legal-page__updated">{page.updated}</p>

        <div className="legal-page__sections">
          {page.sections.map((section) => (
            <section key={section.heading} className="legal-page__section">
              <h2>{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="legal-page__cta">
          <p>For appointment-related questions, contact the clinic directly.</p>
          <Link className="btn btn--primary" to="/#booking">Book an appointment</Link>
        </div>
      </div>
    </main>
  );
}
